// The buyer's-premium schedule's wire form (BL-2482, ADR 060 — Money Type):
// percents as RateWire, amounts as MoneyWire in the record's currency. One
// codec for admin and bidder (ADR 010 — Isomorphic Services).

import { fromWire, toWire, type MoneyWire } from '../money/codec';
import { isSupportedCurrency } from '../money/currency';
import { isWithheldMoney, withheldMoney, type WithheldMoneyWire } from '../money/withheld';
import type { Money } from '../money/money';
import type { RateWire } from '../rate/codec';
import { Rate } from '../rate/rate';
import { premiumScheduleFromStored, type PremiumSchedule } from './schedule';
import { isUsableBuyersPremium, type BuyersPremiumCombineMode, type BuyersPremiumLike } from './types';

/** One band of a tiered schedule on the wire. `threshold: null` is the unbounded remainder. */
export interface PremiumTierWire {
  rate: RateWire;
  threshold: MoneyWire | null;
}

/** Floor and cap on the wire. `null` means none. */
interface PremiumBoundsWire {
  minFee: MoneyWire | null;
  maxFee: MoneyWire | null;
}

/**
 * A {@link PremiumSchedule} as a DTO carries it. Never a number: a rate is its
 * exact fraction, an amount is an exact decimal with its currency.
 */
export type PremiumScheduleWire =
  | ({ type: 'Flat'; rate: RateWire } & PremiumBoundsWire)
  | ({ type: 'Tiered'; combineMode: BuyersPremiumCombineMode; tiers: PremiumTierWire[] } & PremiumBoundsWire);

/**
 * A DTO field carrying a schedule: its wire form, the withheld marker when the
 * record's currency is unsupported (BL-2204: an unreadable value is never `null`,
 * which already means "no schedule"), or `null` when the level has none.
 */
export type PremiumScheduleField = PremiumScheduleWire | WithheldMoneyWire | null;

const moneyToWire = (money: Money | null): MoneyWire | null => (money == null ? null : toWire(money));
const moneyFromWire = (wire: MoneyWire | null): Money | null => (wire == null ? null : fromWire(wire));

/**
 * Encodes a schedule for a DTO.
 *
 * @param schedule - The exact schedule.
 * @returns Its wire form.
 */
export function premiumScheduleToWire(schedule: PremiumSchedule): PremiumScheduleWire {
  const bounds = { minFee: moneyToWire(schedule.minFee), maxFee: moneyToWire(schedule.maxFee) };
  if (schedule.type === 'Flat') return { type: 'Flat', rate: schedule.rate.toWire(), ...bounds };
  return {
    type: 'Tiered',
    combineMode: schedule.combineMode,
    tiers: schedule.tiers.map((t) => ({ rate: t.rate.toWire(), threshold: moneyToWire(t.threshold) })),
    ...bounds,
  };
}

/**
 * Decodes a schedule off a DTO.
 *
 * @param wire - The wire form.
 * @returns The exact schedule.
 * @throws TypeError when a rate or amount is malformed, a currency is unsupported, or a tiered schedule has no tiers.
 */
export function premiumScheduleFromWire(wire: PremiumScheduleWire): PremiumSchedule {
  const bounds = { minFee: moneyFromWire(wire.minFee), maxFee: moneyFromWire(wire.maxFee) };
  if (wire.type === 'Flat') return { type: 'Flat', rate: Rate.fromWire(wire.rate), ...bounds };
  if (!Array.isArray(wire.tiers) || wire.tiers.length === 0) {
    throw new TypeError('A tiered PremiumScheduleWire must carry at least one tier');
  }
  return {
    type: 'Tiered',
    combineMode: wire.combineMode,
    tiers: wire.tiers.map((t) => ({ rate: Rate.fromWire(t.rate), threshold: moneyFromWire(t.threshold) })),
    ...bounds,
  };
}

/**
 * Encodes a STORED (JSONB, number-typed) schedule for a DTO, in the record's
 * currency — the one server seam every response mapper uses.
 *
 * A schedule on a row whose currency the platform does not support is withheld
 * (the BL-2204 marker, naming the currency) rather than read in a guessed one.
 * An absent or structurally unusable schedule, or a row with no currency at
 * all, is `null`.
 *
 * @param bp - The stored schedule.
 * @param currency - The currency of the row that owns it.
 * @returns The field value.
 */
export function storedPremiumScheduleToWire(
  bp: BuyersPremiumLike,
  currency: string | null | undefined,
): PremiumScheduleField {
  if (currency == null) return null;
  if (!isSupportedCurrency(currency)) return isUsableBuyersPremium(bp) ? withheldMoney(currency) : null;
  const schedule = premiumScheduleFromStored(bp, currency);
  return schedule == null ? null : premiumScheduleToWire(schedule);
}

/**
 * Decodes a schedule field — the client-side counterpart of
 * {@link storedPremiumScheduleToWire}. The withheld marker passes through, so
 * the surface can say the schedule exists but cannot be read.
 *
 * @param field - The DTO field (absent is the same as `null`).
 * @returns The exact schedule, the marker, or `null`.
 */
export function premiumScheduleFromField(
  field: PremiumScheduleField | undefined,
): PremiumSchedule | WithheldMoneyWire | null {
  if (field == null) return null;
  return isWithheldMoney(field) ? field : premiumScheduleFromWire(field);
}

/**
 * Whether a value is an exact {@link PremiumSchedule} (every rate a `Rate`)
 * rather than the stored number shape.
 *
 * @param value - Either representation, or nothing.
 * @returns `true` for a PremiumSchedule.
 */
export function isPremiumSchedule(value: unknown): value is PremiumSchedule {
  if (value == null || typeof value !== 'object') return false;
  const v = value as { type?: unknown; rate?: unknown; tiers?: unknown };
  if (v.type === 'Flat') return v.rate instanceof Rate;
  if (v.type === 'Tiered') {
    return (
      Array.isArray(v.tiers) &&
      v.tiers.length > 0 &&
      v.tiers.every((t: { rate?: unknown } | null) => t?.rate instanceof Rate)
    );
  }
  return false;
}
